import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BadgeCheck,
  Building2,
  Eye,
  EyeOff,
  Landmark,
  Pencil,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import {
  ISLAMIC_BANKS,
  NIGERIAN_BANKS,
  formatShortDate,
  validateAccountNumber,
  validateBvn,
} from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  EmptyState,
  Field,
  Input,
  Modal,
  ModalHeader,
  PageHeader,
  Select,
  Skeleton,
} from '@/components/ui';

interface BankAccount {
  id: string;
  bankName: string;
  bankCode: string | null;
  accountNumber: string;
  accountNumberMasked: string;
  accountName: string;
  hasBvn: boolean;
  isPrimary: boolean;
  verifiedAt: string | null;
  createdAt: string;
  disbursementCount: number;
  canDelete: boolean;
}

const BANKS = [
  ...NIGERIAN_BANKS.filter((b) => (ISLAMIC_BANKS as readonly string[]).includes(b.name)),
  ...NIGERIAN_BANKS.filter((b) => !(ISLAMIC_BANKS as readonly string[]).includes(b.name)),
];

export default function BankAccounts() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<BankAccount | 'new' | null>(null);
  const [deleting, setDeleting] = useState<BankAccount | null>(null);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<BankAccount[]>('/members/me/bank-accounts'),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['my-profile'] });
  };

  const setPrimary = useMutation({
    mutationFn: (id: string) => api.post(`/members/me/bank-accounts/${id}/primary`),
    onSuccess: () => {
      toast.success('Primary account updated.');
      invalidate();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ message: string }>(`/members/me/bank-accounts/${id}`),
    onSuccess: (result) => {
      toast.success(result.message);
      setDeleting(null);
      invalidate();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not remove the account.'),
  });

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="Where your money goes"
        title="Bank accounts"
        description="A loan is paid into your primary account. Accounts must be in your own name."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus size={16} /> Add account
          </Button>
        }
      />

      {accounts.length === 0 ? (
        <EmptyState
          icon={<Landmark size={22} />}
          title="No bank account yet"
          description="Add the account you want a loan paid into. You can add more than one and choose which is primary."
          action={
            <Button onClick={() => setEditing('new')}>
              <Plus size={16} /> Add your first account
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {accounts.map((account) => {
            const isRevealed = revealed.has(account.id);
            return (
              <Card key={account.id} className={cn(account.isPrimary && 'ring-1 ring-primary/30')}>
                <CardContent className="p-4 sm:p-5">
                  {/*
                    Bank and actions on one line. The actions used to wrap to a
                    row of their own on a phone, where two bare icons under a
                    card read as decoration rather than as buttons.
                  */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground-secondary">
                        <Building2 size={18} />
                      </span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <p className="font-semibold text-foreground">{account.bankName}</p>
                          {account.isPrimary && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-semibold text-primary">
                              <Star size={10} className="fill-primary" /> Primary
                            </span>
                          )}
                        </div>

                        <div className="mt-1 flex items-center gap-2">
                          <p className="numeric text-sm text-foreground-secondary">
                            {isRevealed ? account.accountNumber : account.accountNumberMasked}
                          </p>
                          {/* Masked by default. Account numbers are read out
                              over the phone and shoulder-surfed in offices. */}
                          <button
                            type="button"
                            onClick={() =>
                              setRevealed((current) => {
                                const next = new Set(current);
                                if (next.has(account.id)) next.delete(account.id);
                                else next.add(account.id);
                                return next;
                              })
                            }
                            className="text-foreground-secondary transition-colors hover:text-primary"
                            aria-label={isRevealed ? 'Hide account number' : 'Show account number'}
                          >
                            {isRevealed ? <EyeOff size={13} /> : <Eye size={13} />}
                          </button>
                        </div>

                        <p className="truncate text-sm text-foreground">{account.accountName}</p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-0.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(account)}
                        aria-label="Edit account"
                      >
                        <Pencil size={15} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!account.canDelete}
                        onClick={() => setDeleting(account)}
                        aria-label="Remove account"
                        title={
                          account.canDelete
                            ? 'Remove account'
                            : 'A loan has been paid into this account, so it cannot be removed'
                        }
                      >
                        <Trash2 size={15} className={account.canDelete ? 'text-danger' : ''} />
                      </Button>
                    </div>
                  </div>

                  {/* Everything secondary on one quiet line beneath. */}
                  <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-border pt-3 text-xs text-foreground-secondary">
                    {account.verifiedAt ? (
                      <Badge tone="success">
                        <BadgeCheck size={11} /> Verified
                      </Badge>
                    ) : (
                      <Badge tone="neutral">Not yet verified</Badge>
                    )}
                    {account.hasBvn && <span>BVN on file</span>}
                    {account.disbursementCount > 0 && (
                      <span>
                        {account.disbursementCount} disbursement
                        {account.disbursementCount === 1 ? '' : 's'}
                      </span>
                    )}
                    <span>Added {formatShortDate(new Date(account.createdAt))}</span>

                    {!account.isPrimary && (
                      <button
                        type="button"
                        onClick={() => setPrimary.mutate(account.id)}
                        className="ml-auto inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                      >
                        <Star size={12} /> Make primary
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Alert tone="info" className="mt-6">
        UNIFEMGA never asks for your bank password or card PIN. If anyone does, it is not us —
        call {' '}
        <a href="tel:+2349168484980" className="font-bold underline">
          (+234) 916 848 4980
        </a>
        .
      </Alert>

      <AccountForm
        account={editing === 'new' ? null : editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          invalidate();
        }}
      />

      <Modal open={deleting !== null} onClose={() => setDeleting(null)} size="sm">
        {deleting && (
          <>
            <ModalHeader
              title="Remove this account?"
              description={`${deleting.bankName} · ${deleting.accountNumberMasked}`}
              onClose={() => setDeleting(null)}
            />
            <div className="space-y-4 p-6">
              <p className="text-sm text-foreground-secondary">
                {deleting.isPrimary
                  ? 'This is your primary account. Another account will be made primary in its place.'
                  : 'This cannot be undone, but you can add the account again later.'}
              </p>
              <div className="flex gap-3">
                <Button
                  variant="danger"
                  full
                  loading={remove.isPending}
                  onClick={() => remove.mutate(deleting.id)}
                >
                  <Trash2 size={16} /> Remove account
                </Button>
                <Button variant="ghost" onClick={() => setDeleting(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function Badge({ tone, children }: { tone: 'success' | 'neutral' | 'info'; children: React.ReactNode }) {
  const classes = {
    success: 'bg-success-soft text-success-soft-foreground ring-success/20',
    neutral: 'bg-neutral-soft text-neutral-soft-foreground ring-neutral/20',
    info: 'bg-info-soft text-info-soft-foreground ring-info/20',
  }[tone];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset',
        classes,
      )}
    >
      {children}
    </span>
  );
}

function AccountForm({
  account,
  open,
  onClose,
  onDone,
}: {
  account: BankAccount | null;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({ bankName: '', accountNumber: '', accountName: '', bvn: '' });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    setForm({
      bankName: account?.bankName ?? '',
      accountNumber: account?.accountNumber ?? '',
      accountName: account?.accountName ?? '',
      bvn: '',
    });
  }, [account, open]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        bankCode: BANKS.find((b) => b.name === form.bankName)?.code,
        bvn: form.bvn || undefined,
      };
      return account
        ? api.patch(`/members/me/bank-accounts/${account.id}`, payload)
        : api.post('/members/me/bank-accounts', payload);
    },
    onSuccess: () => {
      toast.success(account ? 'Account updated.' : 'Bank account added.');
      onDone();
    },
    onError: (error) => {
      if (error instanceof ApiError) setFieldErrors(error.fieldErrors);
      toast.error(error instanceof Error ? error.message : 'Could not save.');
    },
  });

  const changedDetails =
    account !== null &&
    (form.accountNumber !== account.accountNumber || form.bankName !== account.bankName);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader
        title={account ? 'Edit bank account' : 'Add a bank account'}
        description="This must be an account in your own name."
        onClose={onClose}
      />

      <div className="space-y-5 p-6">
        <Field label="Bank" htmlFor="bank" required error={fieldErrors['bankName']}>
          <Select
            id="bank"
            value={form.bankName}
            onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
          >
            <option value="">Select your bank…</option>
            <optgroup label="Non-interest banks">
              {BANKS.filter((b) => (ISLAMIC_BANKS as readonly string[]).includes(b.name)).map(
                (bank) => (
                  <option key={bank.code} value={bank.name}>
                    {bank.name}
                  </option>
                ),
              )}
            </optgroup>
            <optgroup label="All banks">
              {BANKS.filter((b) => !(ISLAMIC_BANKS as readonly string[]).includes(b.name)).map(
                (bank) => (
                  <option key={bank.code} value={bank.name}>
                    {bank.name}
                  </option>
                ),
              )}
            </optgroup>
          </Select>
        </Field>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            label="Account number"
            htmlFor="accountNumber"
            required
            error={fieldErrors['accountNumber'] ?? (validateAccountNumber(form.accountNumber) ?? undefined)}
            hint="10 digits."
          >
            <Input
              id="accountNumber"
              className="numeric tracking-wider"
              inputMode="numeric"
              maxLength={10}
              value={form.accountNumber}
              onChange={(e) =>
                setForm((f) => ({ ...f, accountNumber: e.target.value.replace(/\D/g, '') }))
              }
              placeholder="0123456789"
            />
          </Field>

          <Field
            label="Account name"
            htmlFor="accountName"
            required
            error={fieldErrors['accountName']}
            hint="Exactly as it appears at your bank."
          >
            <Input
              id="accountName"
              value={form.accountName}
              onChange={(e) => setForm((f) => ({ ...f, accountName: e.target.value }))}
            />
          </Field>
        </div>

        <Field
          label={account?.hasBvn ? 'BVN (leave blank to keep the one on file)' : 'BVN'}
          htmlFor="bvn"
          error={fieldErrors['bvn'] ?? (validateBvn(form.bvn) ?? undefined)}
          hint="Optional. 11 digits. Stored encrypted and never shown in full."
        >
          <Input
            id="bvn"
            className="numeric tracking-wider"
            inputMode="numeric"
            maxLength={11}
            value={form.bvn}
            onChange={(e) => setForm((f) => ({ ...f, bvn: e.target.value.replace(/\D/g, '') }))}
          />
        </Field>

        {changedDetails && (
          <Alert tone="warning">
            Changing the bank or account number clears this account&rsquo;s verification. UNIFEMGA
            will need to check the new details before a loan is paid into it.
          </Alert>
        )}

        <div className="flex gap-3 border-t border-border pt-5">
          <Button
            full
            loading={save.isPending}
            disabled={!form.bankName || form.accountNumber.length !== 10 || !form.accountName}
            onClick={() => save.mutate()}
          >
            {account ? 'Save changes' : 'Add account'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
